/**
 * Canonical smart-track label definitions for PureBox.
 *
 * This file is the single source of truth for label metadata consumed by
 * both the backend (LLM prompt, classification) and the frontend (badges,
 * filters, sidebar).
 *
 * Updated: 2026-04-06
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SmartTrackLabelDef {
  /** Machine key (kebab-friendly identifier) */
  key: string
  /** Human-readable label shown in UI */
  label: string
  /** lucide-react icon component name */
  icon: string
  /** Design-system color token path */
  colorToken: string
  /** Hex color value for the label */
  hex: string
}

// ---------------------------------------------------------------------------
// Rendering guidance
// ---------------------------------------------------------------------------

/** Icon sizing constants for smart-track badges. */
export const SMART_TRACK_ICON = {
  /** Desktop icon size in px */
  sizeDesktop: 16,
  /** Mobile icon size in px */
  sizeMobile: 15,
  /** Stroke width for lucide icons */
  stroke: 1.75,
} as const

/**
 * Badge treatment constants.
 *
 * Apply as: `background: hex @ 10%`, `border: hex @ 35%`,
 *           `icon + text: hex @ 100%`.
 */
export const SMART_TRACK_BADGE = {
  /** Background opacity (0–1) */
  bgOpacity: 0.1,
  /** Border opacity (0–1) */
  borderOpacity: 0.35,
  /** Icon & text opacity (always full) */
  fgOpacity: 1,
} as const

// ---------------------------------------------------------------------------
// Label definitions (all 21)
// ---------------------------------------------------------------------------

export const SMART_TRACK_LABEL_DEFS: readonly SmartTrackLabelDef[] = [
  { key: 'security', label: 'Security & account access', icon: 'ShieldCheck', colorToken: 'smartTrack.security', hex: '#475569' },
  { key: 'billing', label: 'Billing & payments', icon: 'CreditCard', colorToken: 'smartTrack.billing', hex: '#059669' },
  { key: 'orders', label: 'Orders & receipts', icon: 'ReceiptText', colorToken: 'smartTrack.orders', hex: '#d97706' },
  { key: 'shipping', label: 'Shipping & delivery', icon: 'Truck', colorToken: 'smartTrack.shipping', hex: '#0284c7' },
  { key: 'returns', label: 'Returns & refunds', icon: 'Undo2', colorToken: 'smartTrack.returns', hex: '#e11d48' },
  { key: 'subscriptions', label: 'Subscriptions & renewals', icon: 'Repeat2', colorToken: 'smartTrack.subscriptions', hex: '#4f46e5' },
  { key: 'support', label: 'Customer support', icon: 'LifeBuoy', colorToken: 'smartTrack.support', hex: '#0d9488' },
  { key: 'product-updates', label: 'Product updates & releases', icon: 'Sparkles', colorToken: 'smartTrack.productUpdates', hex: '#7c3aed' },
  { key: 'newsletters', label: 'Newsletters & marketing', icon: 'Megaphone', colorToken: 'smartTrack.newsletters', hex: '#ea580c' },
  { key: 'personal', label: 'Personal & social', icon: 'Users', colorToken: 'smartTrack.personal', hex: '#db2777' },
  { key: 'work', label: 'Work & internal comms', icon: 'Briefcase', colorToken: 'smartTrack.work', hex: '#1d4ed8' },
  { key: 'meetings', label: 'Meetings & calendar', icon: 'CalendarDays', colorToken: 'smartTrack.meetings', hex: '#0891b2' },
  { key: 'documents', label: 'Documents & collaboration', icon: 'Files', colorToken: 'smartTrack.documents', hex: '#52525b' },
  { key: 'travel', label: 'Travel & tickets', icon: 'Plane', colorToken: 'smartTrack.travel', hex: '#65a30d' },
  { key: 'banking', label: 'Banking & finance', icon: 'Landmark', colorToken: 'smartTrack.banking', hex: '#15803d' },
  { key: 'insurance', label: 'Insurance & benefits', icon: 'ShieldPlus', colorToken: 'smartTrack.insurance', hex: '#57534e' },
  { key: 'government', label: 'Government & taxes', icon: 'Scale', colorToken: 'smartTrack.government', hex: '#b91c1c' },
  { key: 'healthcare', label: 'Healthcare & wellness', icon: 'HeartPulse', colorToken: 'smartTrack.healthcare', hex: '#ef4444' },
  { key: 'education', label: 'Education & careers', icon: 'GraduationCap', colorToken: 'smartTrack.education', hex: '#9333ea' },
  { key: 'utilities', label: 'Utilities & services', icon: 'Wrench', colorToken: 'smartTrack.utilities', hex: '#a16207' },
  { key: 'general', label: 'General', icon: 'Mail', colorToken: 'smartTrack.general', hex: '#6b7280' },
] as const

// ---------------------------------------------------------------------------
// Backward-compatible exports
// ---------------------------------------------------------------------------

/**
 * Plain string array of label names - preserves the original export shape
 * so existing consumers (`SMART_TRACK_LABELS[number]`) keep working.
 */
export const SMART_TRACK_LABELS = SMART_TRACK_LABEL_DEFS.map(
  (d) => d.label,
) as unknown as readonly [
  'Security & account access',
  'Billing & payments',
  'Orders & receipts',
  'Shipping & delivery',
  'Returns & refunds',
  'Subscriptions & renewals',
  'Customer support',
  'Product updates & releases',
  'Newsletters & marketing',
  'Personal & social',
  'Work & internal comms',
  'Meetings & calendar',
  'Documents & collaboration',
  'Travel & tickets',
  'Banking & finance',
  'Insurance & benefits',
  'Government & taxes',
  'Healthcare & wellness',
  'Education & careers',
  'Utilities & services',
  'General',
]

export type SmartTrackLabel = (typeof SMART_TRACK_LABELS)[number]

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

/** Map from label string → full definition (built once). */
const labelByName = new Map<string, SmartTrackLabelDef>(
  SMART_TRACK_LABEL_DEFS.map((d) => [d.label, d]),
)

/** Map from key → full definition (built once). */
const labelByKey = new Map<string, SmartTrackLabelDef>(
  SMART_TRACK_LABEL_DEFS.map((d) => [d.key, d]),
)

/** Look up a label definition by its display name. */
export function getSmartTrackByLabel(
  label: string,
): SmartTrackLabelDef | undefined {
  return labelByName.get(label)
}

/** Look up a label definition by its machine key. */
export function getSmartTrackByKey(
  key: string,
): SmartTrackLabelDef | undefined {
  return labelByKey.get(key)
}
